import { IconType } from 'components/common/icon/IconHandler';
import { AppDispatch } from 'store/store';
import { UserAuth } from 'store/auth';
import { TFunction } from 'i18next';
import { developmentMenuItems } from './menu-data/development';
import { contentMenuItems, contentMenuItemsRegular } from './menu-data/content';
import { adminMenuItems, adminMenuItemsPower } from './menu-data/admin';
import { userMenuItems } from './menu-data/user';
import MenuGlobalDocuments from 'app/navbar/menu-custom-item/NavMenuGlobalDocuments';
import MenuAbout from 'app/navbar/menu-custom-item/NavMenuAbout';
import { userAccessConditionCheckList, userAccessConditionCheckType } from 'hooks/useCheckUserAccess';
import { newMenuItems, newMenuItemsRegular } from 'app/navbar/menu-data/new';
import MenuConcierge from 'app/concierge/menu';

interface MainMenuItemCondition {
    type: userAccessConditionCheckType;
    params?: any;
}

interface MainMenuItemActionParams {
    dispatch: AppDispatch;
    createNewFunc: (url: string, popupSettings?: any) => void;
}

export type mainMenuDataType = 'leftbar' | 'topbar' | 'all';

export interface MainMenuItemSearchData {
    keywords?: {
        entity?: string[];
        entityAlias?: string[];
        manuLabel?: boolean;
        custom?: string[];
    };
    label?: string;
    desc?: string;
    itemType?: 'go_to' | 'create_new' | 'create_new_from' | 'custom_link' | 'recent_item';
    groupKey?: string;
    subGroupKey?: string; //fast workaround for subgroups
    labelSuffix?: 'list' | 'editor' | 'none' | 'page';
}
export interface MainMenuItem {
    label: string;
    children?: MainMenuItemsList;
    href?: string;
    divider?: boolean;
    action?: (params: MainMenuItemActionParams) => void;
    icon?: IconType;
    key: string;
    translate?: boolean;
    isExternal?: boolean;
    newTab?: boolean;
    condition?: MainMenuItemCondition[];
    component?: any;
    searchData?: MainMenuItemSearchData;
}
interface MainMenuItemDivider {
    divider: true;
    condition?: MainMenuItemCondition[];
}

export type MainMenuItemsList = Array<MainMenuItem | MainMenuItemDivider>;

export const NavBarMenuComponentsRegistry: { [key: string]: any } = {
    MenuGlobalDocuments: MenuGlobalDocuments,
    MenuConcierge: MenuConcierge,
    MenuAbout: MenuAbout,
};

//Example to use conditions
/*
 * havePrivilege, adminOrPrivilege
 * condition: [{ type: 'havePrivilege', params: 'PRIV_PORTAL_CSS_IMAGES' }],
 *
 * isAdmin, isSystemAdmin, isPowerUser, isAdminOrPowerUser
 * condition: [{ type: 'isAdmin' }],
 *
 * constantExist
 * condition: [{ type: 'constantExist', params: 'ENABLE_METADATA_COLLECTION' }],
 *
 * constantValueEqual, constantNotExistOrValueEqual
 * condition: [{ type: 'constantValueEqual', params: {name: 'ENABLE_METADATA_COLLECTION', value: true} }],
 *
 * atLeastOneCondition
 * condition: [{ type: 'atLeastOneCondition', params: [{ type: 'isAdmin' }, { type: 'constantExist', params: 'ENABLE_METADATA_COLLECTION' }] }],
 *
 * isTrue
 * condition: [{ type: 'isTrue', params: someBooleanLogic }],
 */

export function filterByCondition(menuData: MainMenuItemsList, checkCondition: userAccessConditionCheckList) {
    return menuData
        .filter((item: any) => {
            return (
                !item.hasOwnProperty('condition') ||
                item?.condition?.every((item: MainMenuItemCondition) => {
                    return checkCondition[item.type](item.params);
                })
            );
        })
        .map((itemOriginal: any) => {
            const item = { ...itemOriginal };
            if (item.children) item.children = filterByCondition(item.children, checkCondition);
            return item;
        });
}

export default function getData(
    t: TFunction,
    state: any,
    checkCondition: userAccessConditionCheckList,
    dataType: mainMenuDataType,
    ignoreThemeSettings?: boolean,
) {
    const userAuth: UserAuth = state.auth;
    const helpLink = state.helpLink.link;
    const customIconAvatar = state.auth?.userInfo?.customIcon;

    const userBrandThemeMenuProperties = state.auth?.userInfo?.brandThemeProperties?.menu;
    const themeEditorMenu = state.themeEditor.menu;
    const themeMenuSettings = { ...(userBrandThemeMenuProperties ?? {}), ...themeEditorMenu };

    const isShowLeftBar = dataType == 'all' || dataType == 'leftbar';
    const isShowTopBar = dataType == 'all' || dataType == 'topbar';
    const isShowByThemeSetting =
        (isShowLeftBar && themeMenuSettings?.main_navigation == 'left') ||
        (isShowTopBar && themeMenuSettings?.main_navigation == 'header');

    let menu: MainMenuItemsList = [];
    if (
        isShowByThemeSetting &&
        process.env.REACT_APP_ENVIRONMENT &&
        process.env.REACT_APP_ENVIRONMENT === 'development'
    )
        menu = [
            {
                label: 'root.development',
                key: 'development',
                children: developmentMenuItems,
                icon: { type: 'mi', value: 'code' },
            },
            {
                divider: true,
            },
        ];

    if (isShowTopBar) {
        menu = [
            ...menu,
            {
                label: '',
                key: 'concierge',
                icon: { type: 'mi', value: 'concierge' },
                component: 'MenuConcierge',
            },
            {
                label: 'root.documents',
                key: 'documents',
                component: 'MenuGlobalDocuments',
            },
        ];
    }

    const isDisplayNewMenu = themeMenuSettings?.new_display != 'N' || ignoreThemeSettings;
    const isDisplayNewMenuPermission =
        themeMenuSettings?.new_display_user_type == 'all' ? true : checkCondition.isAdminOrPowerUser();
    const isDisplayNewMenuCondition = checkCondition.atLeastOneCondition([
        { type: 'isAdminOrPowerUser' },
        { type: 'havePrivilege', params: 'PRIV_CREATE_EDIT_BURSTS' },
        { type: 'havePrivilege', params: 'PRIV_ADD_FOLDERS' },
        { type: 'havePrivilege', params: 'PRIV_CREATE_FTP_CONNECTION' },
    ]);

    const isShowNewMenu =
        isDisplayNewMenu && isDisplayNewMenuPermission && isDisplayNewMenuCondition && isShowByThemeSetting;
    if (isShowNewMenu) {
        menu = [
            ...menu,
            {
                label: 'root.new',
                key: 'new',
                children: checkCondition.isAdminOrPowerUser() ? newMenuItems(userAuth.topMenu) : newMenuItemsRegular,
                icon: { type: 'mi', value: 'new-square' },
                /*condition: [
                    {
                        type: 'atLeastOneCondition',
                        params: [
                            { type: 'isAdminOrPowerUser' },
                            { type: 'havePrivilege', params: 'PRIV_CREATE_EDIT_BURSTS' },
                            { type: 'havePrivilege', params: 'PRIV_ADD_FOLDERS' },
                            { type: 'havePrivilege', params: 'PRIV_CREATE_FTP_CONNECTION' },
                        ],
                    },
                ],*/
            },
        ];
    }

    const isDisplayContentMenu = themeMenuSettings?.content_display != 'N' || ignoreThemeSettings;
    const isDisplayContentMenuPermission =
        themeMenuSettings?.content_display_user_type == 'all' ? true : checkCondition.isAdminOrPowerUser();

    const isShowContentMenu = isDisplayContentMenu && isDisplayContentMenuPermission && isShowByThemeSetting;
    if (isShowContentMenu) {
        menu = [
            ...menu,
            {
                label: 'root.content',
                key: 'editor',
                children: checkCondition.isAdminOrPowerUser() ? contentMenuItems : contentMenuItemsRegular,
                icon: { type: 'mi', value: 'content' },
            },
        ];
    }

    let userInMenuName =
        userAuth.userInfo?.first_name && userAuth.userInfo?.first_name !== ''
            ? userAuth.userInfo.first_name
            : userAuth.userInfo?.username && userAuth.userInfo?.username !== ''
              ? userAuth.userInfo.username
              : '';
    if (userInMenuName) {
        userInMenuName = userInMenuName.charAt(0).toUpperCase() + userInMenuName.slice(1);
    }
    if (isShowByThemeSetting) {
        if (isShowLeftBar) {
            menu = [
                ...menu,
                {
                    label: 'root.admin',
                    key: 'admin',
                    href: '/admin',
                    icon: { type: 'mi', value: 'gear-admin' },
                    condition: [{ type: 'isAdminOrPowerUser' }],
                },
            ];
        }

        if (isShowTopBar) {
            menu = [
                ...menu,
                {
                    label: 'root.admin',
                    key: 'admin',
                    children: checkCondition.isAdmin() ? adminMenuItems : adminMenuItemsPower,
                    icon: { type: 'mi', value: 'gear-admin' },
                    condition: [{ type: 'isAdminOrPowerUser' }],
                },
            ];

            //Add divider is show one of previous menu item (New, Content, Admin) and it's Topbar
            if (isShowNewMenu || isShowContentMenu || checkCondition.isAdminOrPowerUser()) {
                menu = [
                    ...menu,
                    {
                        divider: true,
                    },
                ];
            }
        }
    }
    if (isShowTopBar) {
        menu = [
            ...menu,
            {
                label: userInMenuName,
                key: 'user',
                icon: userAuth.userInfo?.hijack_username
                    ? { type: 'mi', value: 'spy' }
                    : customIconAvatar !== ''
                      ? { type: 'upload', value: customIconAvatar }
                      : { type: 'mi', value: 'user-profile' },
                children: userMenuItems(userAuth, t, helpLink, themeMenuSettings, ignoreThemeSettings),
            },
        ];
    }

    return filterByCondition(menu, checkCondition);
}
